import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { isApiError } from '@/api/errors';
import { baseApi as api } from '.';
import type { TimelineQuery } from './baseApi';
import { baseQueryKeys } from './queryKeys';
import type { SeasonQuery, SportListKind, SportListQuery } from './types';

/** How often the app re-checks a service that is not answering after Windows starts. */
export const SERVICE_RECHECK_MS = 5_000;

const isSignedOut = (error: unknown) => isApiError(error) && error.status === 401;

export function useBootstrap() {
  return useQuery({
    queryKey: baseQueryKeys.bootstrap,
    queryFn: () => api.bootstrap(),
    retry: (count, error) => !isSignedOut(error) && count < 2,
    retryDelay: 500,
    staleTime: Infinity,
    refetchInterval: (query) => (query.state.status === 'error' && !isSignedOut(query.state.error) ? SERVICE_RECHECK_MS : false),
  });
}

export const useSearch = (query: string) =>
  useQuery({ queryKey: baseQueryKeys.search(query), queryFn: () => api.search(query), enabled: query.trim().length > 0, placeholderData: keepPreviousData, staleTime: 30_000 });

export const useSports = () => useQuery({ queryKey: baseQueryKeys.sports.list, queryFn: () => api.sports.list(), staleTime: 60_000 });
export const useSport = (id: string | undefined) => useQuery({ queryKey: baseQueryKeys.sports.detail(id), queryFn: () => api.sports.get(id!), enabled: !!id });

export const useSportList = (kind: SportListKind, query: SportListQuery = {}, options: { enabled?: boolean } = {}) =>
  useQuery({ queryKey: baseQueryKeys.sportLists.list(kind, query), queryFn: () => api.sportLists.list(kind, query), staleTime: 30_000, enabled: options.enabled ?? true });

export const useSeasons = (query: SeasonQuery = {}, options: { enabled?: boolean } = {}) =>
  useQuery({ queryKey: baseQueryKeys.seasons.list(query), queryFn: () => api.seasons.list(query), staleTime: 60_000, enabled: options.enabled ?? true });
export const useSeason = (id: string | undefined) => useQuery({ queryKey: baseQueryKeys.seasons.detail(id), queryFn: () => api.seasons.get(id!), enabled: !!id });

export const useLocations = () => useQuery({ queryKey: baseQueryKeys.locations, queryFn: () => api.locations.list(), staleTime: 60_000 });
export const useReportSettings = () => useQuery({ queryKey: baseQueryKeys.reportSettings, queryFn: () => api.reportSettings.get(), staleTime: 30_000 });

export const useAuditEvents = (action = '') =>
  useInfiniteQuery({
    queryKey: baseQueryKeys.audit(action),
    queryFn: ({ pageParam }) => api.audit.list({ action: action || undefined, before: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextBefore ?? undefined,
  });

export const useTimeline = (query: TimelineQuery) => useQuery({ queryKey: baseQueryKeys.timeline(query), queryFn: () => api.timeline(query) });
export const useApps = () => useQuery({ queryKey: baseQueryKeys.apps, queryFn: () => api.apps.list() });
export const useBackupStatus = () => useQuery({ queryKey: baseQueryKeys.backups.status, queryFn: () => api.backups.status() });
export const useBackupHistory = () => useQuery({ queryKey: baseQueryKeys.backups.history, queryFn: () => api.backups.history() });
export const useSystemChecks = () => useQuery({ queryKey: baseQueryKeys.systemChecks, queryFn: () => api.system.checks() });
export const useWelcome = () => useQuery({ queryKey: baseQueryKeys.welcome, queryFn: () => api.welcome(), staleTime: 30_000, retry: 1, retryDelay: 500 });
export const useUsers = (options: { enabled?: boolean } = {}) => useQuery({ queryKey: baseQueryKeys.users, queryFn: () => api.users.list(), enabled: options.enabled ?? true });
export const useRoles = () => useQuery({ queryKey: baseQueryKeys.roles, queryFn: () => api.roles.list(), staleTime: 60_000 });
